import type { Escola, ModalidadeEnsino } from '../types/database'
import { turnosFromEscola } from './turnosEscola'

/** Identificador da modalidade na camada de UI (nossa nomenclatura). */
export type ModalidadeGrupoId = 'infantil' | 'fund1' | 'fund2' | 'eja' | 'integral'

export type ModalidadeGrupo = {
  id: ModalidadeGrupoId
  label: string
  keys: readonly ModalidadeEnsino[]
  ativo: boolean
  /** Chave PNAE usada na importação quando há divergência (ex.: Infantil → Pré). */
  importPnaeKey: ModalidadeEnsino
  colunasAlunos: readonly (keyof Escola)[]
  colunaAlunosPrincipal: keyof Escola
}

export const MODALIDADE_GRUPOS: readonly ModalidadeGrupo[] = [
  {
    id: 'infantil',
    label: 'Infantil',
    /** Canônico no banco: só `pre`. UI rotula como Infantil (sem espelho creche). */
    keys: ['pre'],
    ativo: true,
    importPnaeKey: 'pre',
    colunasAlunos: ['alunos_creche', 'alunos_pre'],
    colunaAlunosPrincipal: 'alunos_pre',
  },
  {
    id: 'fund1',
    label: 'Fundamental Menor',
    keys: ['fund1'],
    ativo: true,
    importPnaeKey: 'fund1',
    colunasAlunos: ['alunos_fund1'],
    colunaAlunosPrincipal: 'alunos_fund1',
  },
  {
    id: 'fund2',
    label: 'Fundamental Maior',
    keys: ['fund2'],
    ativo: true,
    importPnaeKey: 'fund2',
    colunasAlunos: ['alunos_fund2'],
    colunaAlunosPrincipal: 'alunos_fund2',
  },
  {
    id: 'eja',
    label: 'EJA',
    keys: ['eja'],
    ativo: true,
    importPnaeKey: 'eja',
    colunasAlunos: ['alunos_eja'],
    colunaAlunosPrincipal: 'alunos_eja',
  },
  {
    id: 'integral',
    label: 'Integral',
    keys: ['integral'],
    // Turno I usa cardápio/per capita do nível (pré, fund1, fund2, eja) — sem modalidade própria
    ativo: false,
    importPnaeKey: 'integral',
    colunasAlunos: ['alunos_integral'],
    colunaAlunosPrincipal: 'alunos_integral',
  },
] as const

export const MODALIDADE_GRUPOS_ATIVOS = MODALIDADE_GRUPOS.filter((g) => g.ativo)

/** Modalidades ativas com alunos, agrupadas para seletores de UI. */
export function modalidadeGruposAtivosEscolas(escolas: Escola[]) {
  const ativos = new Set<ModalidadeGrupoId>()

  for (const escola of escolas) {
    const infantil = turnosFromEscola(escola, 'infantil')
    const fundMenor = turnosFromEscola(escola, 'fund_menor')
    const fundMaior = turnosFromEscola(escola, 'fund_maior')
    const eja = turnosFromEscola(escola, 'eja')
    if (infantil.total > 0 || (escola.alunos_creche || 0) > 0) ativos.add('infantil')
    if (fundMenor.total > 0) ativos.add('fund1')
    if (fundMaior.total > 0) ativos.add('fund2')
    if (eja.total > 0) ativos.add('eja')
    // CEACT conta no cardápio fund1 — garantir grupo ativo se só houver CEACT
    if ((escola.alunos_ceact || 0) > 0) ativos.add('fund1')

    // Fallback legado (escolas sem colunas de turno preenchidas)
    if (!infantil.total && !fundMenor.total && !fundMaior.total && !eja.total) {
      for (const grupo of MODALIDADE_GRUPOS_ATIVOS) {
        const total = grupo.colunasAlunos.reduce((s, col) => s + (Number(escola[col]) || 0), 0)
        if (total > 0) ativos.add(grupo.id)
      }
    }
  }

  const filtrados = MODALIDADE_GRUPOS_ATIVOS.filter((g) => ativos.has(g.id))
  return filtrados.length > 0 ? filtrados : MODALIDADE_GRUPOS_ATIVOS
}

/** Rótulo de exibição para uma chave PNAE do banco. */
export function labelModalidadeEnsino(modalidade: ModalidadeEnsino): string {
  if (modalidade === 'creche' || modalidade === 'pre') return 'Infantil'
  if (modalidade === 'medio') return 'Médio'
  const grupo = MODALIDADE_GRUPOS_ATIVOS.find((g) => g.keys.includes(modalidade))
  return grupo?.label ?? modalidade
}

/** Chaves PNAE visíveis na UI (exclui médio). */
export const MODALIDADES_UI_ATIVAS: ModalidadeEnsino[] = [
  ...new Set(MODALIDADE_GRUPOS_ATIVOS.flatMap((g) => [...g.keys])),
]
